/**
 * Language Selector Component
 * Allows users to switch between Japanese and English
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { changeLanguage, getAvailableLanguages } from '../lib/i18n';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../lib/api-client';

interface LanguageSelectorProps {
    className?: string;
}

export default function LanguageSelector({ className = '' }: LanguageSelectorProps) {
    const { t, i18n } = useTranslation('common');
    const { user, isAuthenticated } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const languages = getAvailableLanguages();
    const currentLang = i18n.language;

    const handleLanguageChange = async (langCode: string) => {
        await changeLanguage(langCode);
        setIsOpen(false);
        // Save to backend if logged in as viewer (role can be 'VIEWER' or 'USER')
        if (isAuthenticated && (user?.role === 'VIEWER' || user?.role === 'USER')) {
            try {
                await apiClient.updateLanguage(langCode);
            } catch (err) {
                console.error('Failed to save language preference to server:', err);
            }
        }
    };

    const currentLanguage = languages.find(l => l.code === currentLang) || languages[0];

    return (
        <div className={`relative ${className}`}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-1 px-2 py-1 text-sm bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors"
                aria-label={t('language.switch')}
            >
                <span className="text-base">🌐</span>
                <span>{currentLanguage.nativeName}</span>
                <span className="text-xs">▼</span>
            </button>

            {isOpen && (
                <>
                    {/* Backdrop */}
                    <div
                        className="fixed inset-0 z-40"
                        onClick={() => setIsOpen(false)}
                    />
                    {/* Dropdown */}
                    <div className="absolute right-0 mt-1 bg-white rounded shadow-lg border z-50 min-w-[120px]">
                        {languages.map((lang) => (
                            <button
                                key={lang.code}
                                onClick={() => handleLanguageChange(lang.code)}
                                className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 transition-colors ${
                                    lang.code === currentLang ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                                }`}
                            >
                                {lang.nativeName}
                            </button>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
